import { type QueueTab, SuggestionQueue } from "@/components/analysis/suggestion-queue";
import { ProjectMeetingInvite } from "@/components/projects/project-meeting-invite";
import { paginationSearchFields } from "@/lib/search";
import { Navigate, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * `/projects/:slug/suggestions` — what Hubert has proposed **about this
 * project**, decided by the people who run it.
 *
 * The cross-project queue on `/suggestions/work-items` stays admin-only: it
 * spans projects a manager has no business triaging. This tab is the same
 * queue narrowed to one project, and the narrowing is the permission — the
 * server checks that the viewer manages the project it's asked for, and won't
 * serve an unscoped read to anyone but an admin.
 *
 * Below the queue is the other half of the manager's control over Hubert:
 * inviting him into a Discord call for this project.
 *
 * E2E behaviour contract: `e2e/projects/project-suggestions.spec.ts`.
 */

const searchSchema = z.object({
  status: z.enum(["pending", "approved", "rejected", "all"]).optional(),
  ...paginationSearchFields,
});

export const Route = createFileRoute("/_app/projects/$slug/suggestions")({
  validateSearch: searchSchema,
  component: ProjectSuggestionsPage,
});

function ProjectSuggestionsPage() {
  const { project, canEdit } = useLoaderData({ from: "/_app/projects/$slug" });
  const search = Route.useSearch();
  const params = Route.useParams();
  const navigate = useNavigate();
  const tab: QueueTab = search.status ?? "pending";
  // Bumped when a meeting Hubert recorded finishes, so its fresh proposals
  // show up without a manual reload.
  const [reloadKey, setReloadKey] = useState(0);

  // The tab isn't offered to non-managers, but a typed-in URL has to bounce
  // too — the server would refuse anyway, and a 403 shell is a worse answer
  // than the project they were looking at.
  if (!canEdit) {
    return <Navigate to="/projects/$slug" params={{ slug: params.slug }} />;
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Suggestions</h1>
        <p className="text-muted-foreground mt-1">
          What Hubert proposes about {project.name} — from meetings, forwarded client emails,
          WhatsApp and Discord. Approving one applies it through the normal flow, under your name;
          rejecting drops it.
        </p>
      </div>

      <SuggestionQueue
        tab={tab}
        onTabChange={(t) =>
          navigate({
            to: "/projects/$slug/suggestions",
            params: { slug: params.slug },
            // A new tab is a new list — staying on page 4 of it would land on
            // an empty page as often as not.
            search: { ...search, status: t === "pending" ? undefined : t, page: undefined },
          })
        }
        reloadKey={reloadKey}
        projects={[{ id: project.id, name: project.name }]}
        projectId={project.id}
        page={search.page}
        pageSize={search.pageSize}
        basePath={`/projects/${params.slug}/suggestions`}
        searchParams={{ status: search.status, pageSize: search.pageSize }}
        emptyPendingText="Nothing waiting for this project — proposals from meetings, emails and chats land here."
      />

      <ProjectMeetingInvite slug={params.slug} onCompleted={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
